// shared/pdf/documents/TrialBalanceDocument.jsx
import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import { fmtINR } from '../pdfUtils';

const fmtDisplay = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3) {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${parts[2]} ${months[parseInt(parts[1], 10) - 1]} ${parts[0]}`;
  }
  return d;
};

const s = StyleSheet.create({
  page:     { fontFamily: 'NotoSans', paddingBottom: 60, fontSize: 8.5 },
  content:  { marginHorizontal: 20, marginTop: 0 },

  // Meta strip (financial year + as-on date)
  metaStrip: {
    flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
    paddingVertical: 6, paddingHorizontal: 20,
    borderBottomWidth: 1, borderBottomStyle: 'solid',
  },
  metaItem: { flexDirection: 'row', alignItems: 'center', marginHorizontal: 14 },
  metaLbl:  { fontSize: 7.5, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4 },
  metaVal:  { fontSize: 8, fontWeight: 'bold' },

  // Table
  table: {
    borderWidth: 1, borderStyle: 'solid', marginTop: 10,
  },
  headRow: {
    flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 5,
    borderBottomWidth: 1, borderBottomStyle: 'solid',
  },
  headName: { fontSize: 8, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 },
  headNum:  { fontSize: 8, fontWeight: 'bold', textTransform: 'uppercase', width: 100, textAlign: 'right' },

  // Group header
  groupRow: {
    flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 5,
    borderBottomWidth: 0.5, borderBottomStyle: 'solid',
  },
  groupLbl: { fontSize: 8.5, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 },
  groupSpacer: { width: 200 },

  // Account row
  dataRow:  { flexDirection: 'row', paddingLeft: 18, paddingRight: 6, paddingVertical: 3.5, minHeight: 16 },
  nameCell: { fontSize: 8.5, flex: 1 },
  numCell:  { fontSize: 8.5, width: 100, textAlign: 'right' },
  dashCell: { fontSize: 8.5, width: 100, textAlign: 'right' },

  // Sub-total
  subRow: {
    flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 4,
    borderTopWidth: 0.5, borderTopStyle: 'solid',
  },
  subLbl: { fontSize: 8, flex: 1 },
  subNum: { fontSize: 8, width: 100, textAlign: 'right' },

  // Grand total
  grandRow: {
    flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 5,
    borderTopWidth: 2, borderTopStyle: 'solid',
  },
  grandLbl: { fontSize: 9, fontWeight: 'bold', flex: 1 },
  grandNum: { fontSize: 9, fontWeight: 'bold', width: 100, textAlign: 'right' },

  statusWrap: { alignItems: 'center', marginTop: 8 },
  statusTxt:  { fontSize: 8.5, fontWeight: 'bold' },
});

export default function TrialBalanceDocument({ data, theme: themeKey, orientation, sections }) {
  const t    = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];
  const rows = Array.isArray(data?.rows) ? data.rows : [];

  // Financial year & as-on date from documentProps
  const yearLabel = data?.filterYear || '';
  const asOnDate  = data?.asOnDate   ? fmtDisplay(data.asOnDate) : '';

  // Group rows by ledger group name, preserving order
  const groupMap   = {};
  const groupOrder = [];
  rows.forEach((r) => {
    const g = r.groupName || 'Other';
    if (!groupMap[g]) { groupMap[g] = []; groupOrder.push(g); }
    groupMap[g].push(r);
  });

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'portrait'} style={[s.page, { backgroundColor: t.white }]}>
        {/* Derasar name, address, phone, email */}
        {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Trial Balance" theme={t} />}

        {/* Financial Year + As-on Date strip */}
        {(yearLabel || asOnDate) && (
          <View style={[s.metaStrip, { backgroundColor: t.sectionBg, borderBottomColor: t.borderLight }]}>
            {yearLabel && (
              <View style={s.metaItem}>
                <Text style={[s.metaLbl, { color: t.muted }]}>Financial Year:</Text>
                <Text style={[s.metaVal,  { color: t.body  }]}>{yearLabel}</Text>
              </View>
            )}
            {asOnDate && (
              <View style={s.metaItem}>
                <Text style={[s.metaLbl, { color: t.muted }]}>As on Date:</Text>
                <Text style={[s.metaVal,  { color: t.body  }]}>{asOnDate}</Text>
              </View>
            )}
          </View>
        )}

        <View style={s.content}>
          <View style={[s.table, { borderColor: t.borderMid }]}>

            {/* Column headers */}
            <View style={[s.headRow, { backgroundColor: t.dark, borderBottomColor: t.borderMid }]}>
              <Text style={[s.headName, { color: t.headerText }]}>Account Name</Text>
              <Text style={[s.headNum,  { color: t.headerText }]}>Debit (Rs.)</Text>
              <Text style={[s.headNum,  { color: t.headerText }]}>Credit (Rs.)</Text>
            </View>

            {/* Grouped account rows */}
            {groupOrder.map((groupName) => {
              const gRows = groupMap[groupName];
              const subDr = gRows.reduce((s, r) => s + r.drBalance, 0);
              const subCr = gRows.reduce((s, r) => s + r.crBalance, 0);

              return (
                <View key={groupName}>
                  {/* Group header */}
                  <View style={[s.groupRow, { backgroundColor: t.light, borderBottomColor: t.borderLight }]}>
                    <Text style={[s.groupLbl, { color: t.body }]}>{groupName}</Text>
                    <View style={s.groupSpacer} />
                  </View>

                  {/* Account rows */}
                  {gRows.map((row, i) => (
                    <View
                      key={i}
                      style={[s.dataRow, { backgroundColor: i % 2 === 0 ? t.white : t.sectionBg }]}
                      wrap={false}
                    >
                      <Text style={[s.nameCell, { color: t.body }]}>{row.accountName}</Text>
                      {row.drBalance > 0
                        ? <Text style={[s.numCell, { color: t.body }]}>{fmtINR(row.drBalance)}</Text>
                        : <Text style={[s.dashCell, { color: t.textFaint }]}>—</Text>}
                      {row.crBalance > 0
                        ? <Text style={[s.numCell, { color: t.body }]}>{fmtINR(row.crBalance)}</Text>
                        : <Text style={[s.dashCell, { color: t.textFaint }]}>—</Text>}
                    </View>
                  ))}

                  {/* Sub-total */}
                  <View style={[s.subRow, { backgroundColor: t.subtotalBg, borderTopColor: t.borderLight }]} wrap={false}>
                    <Text style={[s.subLbl, { color: t.textTertiary }]}>Sub-Total — {groupName}</Text>
                    <Text style={[s.subNum, { color: t.textTertiary }]}>{subDr > 0 ? fmtINR(subDr) : '—'}</Text>
                    <Text style={[s.subNum, { color: t.textTertiary }]}>{subCr > 0 ? fmtINR(subCr) : '—'}</Text>
                  </View>
                </View>
              );
            })}

            {/* Grand Total */}
            <View style={[s.grandRow, { backgroundColor: t.mid, borderTopColor: t.borderDark }]}>
              <Text style={[s.grandLbl, { color: t.body }]}>Grand Total</Text>
              <Text style={[s.grandNum, { color: t.body }]}>{fmtINR(data?.grandTotalDr)}</Text>
              <Text style={[s.grandNum, { color: t.body }]}>{fmtINR(data?.grandTotalCr)}</Text>
            </View>

          </View>

          {/* Balance status */}
          {sections.summaryCards && (
            <View style={s.statusWrap}>
              <Text style={[s.statusTxt, { color: data?.isBalanced ? t.statusGreen : t.statusRed }]}>
                {data?.isBalanced
                  ? 'Trial Balance is Balanced  ✓'
                  : `Out of Balance by ${fmtINR(Math.abs((data?.grandTotalDr ?? 0) - (data?.grandTotalCr ?? 0)))}`}
              </Text>
            </View>
          )}
        </View>

        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}


